import React, { useState } from 'react';
import { Search, Factory, ShoppingCart, Truck, ChevronRight, AlertCircle } from 'lucide-react';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import RequestForm from './RequestForm';
import { useLanguage } from '../../contexts/LanguageContext';

function SearchAndPurchasePage() {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    productLink: '',
    comment: '',
    name: '',
    phone: '',
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const errors = {
      name: '',
      phone: '',
    };
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Пожалуйста, введите имя';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Пожалуйста, введите телефон';
      isValid = false;
    } else if (!/^\+?[0-9\s-()]+$/.test(formData.phone)) {
      errors.phone = 'Пожалуйста, введите корректный номер телефона';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Here you would typically send the data to your backend
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setSubmitSuccess(true);
      setFormData({
        productLink: '',
        comment: '',
        name: '',
        phone: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="pt-20 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="mb-12">
            <Breadcrumbs />
          </div>
          <div className="max-w-[780px]">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">
                {language === 'ru' ? 'Поиск и выкуп товаров' : 
                 language === 'en' ? 'Product Sourcing' : 
                 '产品采购'}
              </span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                {language === 'ru' ? 'из Китая' : 
                 language === 'en' ? 'from China' : 
                 '从中国'}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              {language === 'ru' 
                ? 'Профессиональная помощь в поиске товаров и производителей, выкуп с китайских площадок и прямое взаимодействие с фабриками'
                : language === 'en' 
                ? 'Professional assistance in finding products and manufacturers, purchasing from Chinese platforms and direct interaction with factories'
                : '专业协助寻找产品和制造商，从中国平台购买并与工厂直接互动'}
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid with Elevated Cards */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B3BA0] mb-6">
              {language === 'ru' ? 'Наши услуги' : 
               language === 'en' ? 'Our Services' : 
               '我们的服务'}
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              {language === 'ru' 
                ? 'Предлагаем полный комплекс услуг по поиску и закупке товаров в Китае для вашего бизнеса.'
                : language === 'en'
                ? 'We offer a full range of services for finding and purchasing goods in China for your business.'
                : '我们为您的业务提供在中国寻找和购买商品的全方位服务。'}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Search className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Поиск товаров' : 
                          language === 'en' ? 'Product Search' : 
                          '产品搜索',
                  description: language === 'ru' ? 'Работаем с площадками 1688, Taobao и другими маркетплейсами Китая' : 
                               language === 'en' ? 'We work with 1688, Taobao and other Chinese marketplaces' : 
                               '我们与1688、淘宝和其他中国市场合作'
                },
                {
                  icon: <Factory className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Прямой контакт' : 
                          language === 'en' ? 'Direct Contact' : 
                          '直接联系',
                  description: language === 'ru' ? 'Находим производителей напрямую и ведём переговоры от вашего имени' : 
                               language === 'en' ? 'We find manufacturers directly and negotiate on your behalf' : 
                               '我们直接寻找制造商并代表您进行谈判'
                },
                {
                  icon: <ShoppingCart className="w-12 h-12 text-[#1B3BA0]" />,
                  title: language === 'ru' ? 'Полное сопровождение' : 
                          language === 'en' ? 'Full Support' : 
                          '全程支持',
                  description: language === 'ru' ? 'Организуем выкуп, проверку качества и доставку товара' : 
                               language === 'en' ? 'We organize purchase, quality control and delivery of goods' : 
                               '我们组织购买、质量控制和货物交付'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps with Gradient Background */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            {language === 'ru' ? 'Как мы работаем' : 
             language === 'en' ? 'How We Work' : 
             '我们如何工作'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: language === 'ru' ? 'Заявка' : 
                        language === 'en' ? 'Request' : 
                        '请求',
                description: language === 'ru' ? 'Вы оставляете заявку с описанием нужного товара или ссылкой на аналог' : 
                             language === 'en' ? 'You leave a request with a description of the desired product or a link to an analog' : 
                             '您留下请求，描述所需产品或提供类似产品的链接'
              },
              {
                step: '02',
                title: language === 'ru' ? 'Поиск и согласование' : 
                        language === 'en' ? 'Search and Approval' : 
                        '搜索和批准',
                description: language === 'ru' ? 'Мы находим поставщиков, сравниваем цены и условия, согласовываем с вами' : 
                             language === 'en' ? 'We find suppliers, compare prices and conditions, coordinate with you' : 
                             '我们寻找供应商，比较价格和条件，与您协调'
              },
              {
                step: '03',
                title: language === 'ru' ? 'Выкуп и доставка' : 
                        language === 'en' ? 'Purchase and Delivery' : 
                        '购买和交付',
                description: language === 'ru' ? 'Организуем выкуп товара, проверку качества и доставку до вашего склада' : 
                             language === 'en' ? 'We organize the purchase of goods, quality control and delivery to your warehouse' : 
                             '我们组织货物的购买、质量控制和交付到您的仓库'
              }
            ].map((phase, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="text-6xl font-bold text-[#1B3BA0]/10 mb-6">{phase.step}</div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{phase.title}</h3>
                <p className="text-gray-600">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            {language === 'ru' ? 'Получить расчёт стоимости' : 
             language === 'en' ? 'Get a Cost Estimate' : 
             '获取费用估算'}
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            {language === 'ru' 
              ? 'Заполните форму, и мы свяжемся с вами для обсуждения деталей'
              : language === 'en' 
              ? 'Fill out the form, and we will contact you to discuss the details'
              : '填写表格，我们将联系您讨论详情'}
          </p>
          <RequestForm source={language === 'ru' ? "Поиск и выкуп товаров" : language === 'en' ? "Product Sourcing" : "产品采购"} />
        </div>
      </section>
    </div>
  );
}

export default SearchAndPurchasePage;